import React, { useState, useContext, useEffect, useRef, memo } from 'react';
import { AuthContext } from "../.././../../../../context/AuthContext";
import NotificationContext from "../../../../../../context/NotificationContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  LButton
} from "@loffa/gpp-modules";
import styles from "./UserTableOktaStatusCell.module.css";

// Kendo's grid can mount this cell more than once in quick succession for the
// same row (e.g. during export). This cache de-dupes concurrent requests for
// the same user so repeated mounts share one in-flight fetch instead of each
// firing its own GetOktaStatus call.
const inFlightOktaStatusRequests = new Map();

function fetchOktaStatus(gatewayBase, userGuid, accessToken) {
  if (inFlightOktaStatusRequests.has(userGuid)) {
    return inFlightOktaStatusRequests.get(userGuid);
  }

  const url = gatewayBase + "/multitenancy/api/Users/" + userGuid + "/GetOktaStatus";
  const request = fetch(url, {
    method: "GET",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
    .then(response => {
      if (response.ok) {
        return response.text();
      }
      else {
        throw new Error("Error getting Okta Status: " + response.status + " " + response.statusText);
      }
    })
    .finally(() => {
      inFlightOktaStatusRequests.delete(userGuid);
    });

  inFlightOktaStatusRequests.set(userGuid, request);
  return request;
}

function UserTableOktaStatusCell(props) {
  const [data, setData] = useState(null);
  const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;
  const authContext = useContext(AuthContext);
  const notificationContext = useContext(NotificationContext);
  const accessToken = authContext.authState().accessToken.accessToken;

  // NotificationContext rebuilds its value object on every render (unrelated
  // to this component), so it's read via a ref instead of a dependency to
  // avoid re-firing the fetch below on every incidental context change.
  const notificationContextRef = useRef(notificationContext);
  notificationContextRef.current = notificationContext;

  useEffect(() => {
    let isMounted = true;
    setData(null);

    fetchOktaStatus(GATEWAY_BASE, props.userGuid, accessToken)
      .then(res => {
        if (!isMounted) return;

        var titleCaseData = res;

        if (titleCaseData !== 'N/A') {
          titleCaseData = titleCaseData.replace(/\w\S*/g, function (txt) { return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(); });
        }

        setData(titleCaseData);
      })
      .catch(error => {
        if (!isMounted) return;
        notificationContextRef.current.addError(error.message);
      });

    return () => {
      isMounted = false;
    };
  }, [GATEWAY_BASE, props.userGuid, props.oktaId, accessToken]);

  let onResendEmailClick = async (oktaId, sentEmail) => {
    let url = GATEWAY_BASE + "/identity/api/Users/ByOktaId/" + oktaId + "/reactivateUser";

    try {
      await fetch(url, {
        method: "POST",
        mode: "cors",
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authContext.authState().accessToken.accessToken}`,
        }),
        body: JSON.stringify({
          sendEmail: true,
          email: sentEmail
        }),
      })
        .then(response => {
          if (!response.ok) {
            throw new Error("Error trying to resend: " + response.status + " " + response.statusText);
          }
        })
        .then(json => {
          notificationContext.addSuccess("Resent Welcome Email to " + sentEmail);
        });
    } catch (error) {
      notificationContext.addError(error.message);
    };
  };

  if (!data) {
    return <span>Loading...</span>;
  }

  return (
    <span>
      {data}
      {data !== "Provisioned" ?
        null
        :
        (
          <LButton
            className={styles.resendBtn}
            themeColor="primary"
            tooltip="Resend Welcome Email"
            tooltipPosition="topRight"
            onClick={() => onResendEmailClick(props.oktaId, props.userEmail)}
          >
            <FontAwesomeIcon
              icon="fa-solid fa-envelope"
            />
          </LButton>)
      }
    </span>
  );
}

function propsAreEqual(prevProps, nextProps) {
  return prevProps.userGuid === nextProps.userGuid && prevProps.oktaId === nextProps.oktaId;
}

export default memo(UserTableOktaStatusCell, propsAreEqual);
